import { useSearchParams } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';

import {
  Button,
  FloatingTabs,
  Icon,
  Link,
  RyeconClock,
  RyeconColorSnowflake,
  RyeconDatabase,
  RyeconDatabaseSchema,
  Sizes,
  Tooltip,
} from '@dbt-labs/sourdough';

import { SchemaNode, WarehouseEntityType } from '@/__generated__/graphql';
import { Page } from '@/components/Page';
import { LinkCell } from '@/components/Table/LinkCell';
import { useLinkToExplorer } from '@/dbt-explorer';
import { useFavoriteAssets } from '@/hooks/admin/favoriteAssets';
import { useWarehouseEntities } from '@/hooks/discovery/catalog/entities';
import { useValidatedParams } from '@/hooks/params';
import { FavoriteAssetToggle } from '@/pages/account/components/FavoriteToggle';
import { ResourceFilterTable } from '@/pages/account/project/resource/components/ResourceFilterTable';
import {
  HeaderIconItem,
  TextIconPair,
} from '@/pages/account/project/resource/details/components/Header';
import { formatDateCommon, toRelativeDate } from '@/util/date';
import { formatBytes } from '@/util/formatBytes';
import { AssetBreadcrumbs } from '../AssetBreadcrumbs';

const assetIconMapping = {
  [WarehouseEntityType.Database]: RyeconDatabase,
  [WarehouseEntityType.Schema]: RyeconDatabaseSchema,
};

const _tabTypes = ['tables', 'views'] as const;
type TabType = (typeof _tabTypes)[number];

const tabNameMap: Record<TabType, string> = {
  tables: 'Tables',
  views: 'Views',
};

export const SchemaFilterPage = ({ asset }: { asset: SchemaNode | undefined }) => {
  const { accountId, assetId } = useValidatedParams();
  const pageSize = 10;
  const catalogAssetLink = useLinkToExplorer('catalogAsset');

  const [tab, setTab] = useSearchParams();

  const {
    data: views,
    fetching: viewsFetching,
    pageInfo: viewsPageInfo,
    fetchMore: viewsFetchMore,
  } = useWarehouseEntities({
    accountId,
    first: pageSize,
    filter: {
      urn: '',
      type: WarehouseEntityType.View,
      parent_urn: assetId ?? '',
    },
  });

  const {
    data: tables,
    fetching: tablesFetching,
    pageInfo: tablesPageInfo,
    fetchMore: tablesFetchMore,
  } = useWarehouseEntities({
    accountId,
    first: pageSize,
    filter: {
      urn: '',
      type: WarehouseEntityType.Table,
      parent_urn: assetId ?? '',
    },
  });

  const { data: favoriteAssets } = useFavoriteAssets(accountId);
  const isFavorite = favoriteAssets?.some(
    (favoriteAsset) => favoriteAsset.asset_id === asset?.urn,
  );

  const headerIcons: TextIconPair[] = [];

  if (asset?.type) {
    headerIcons.push({
      ryecon: assetIconMapping[asset.type as keyof typeof assetIconMapping],
      text: `${asset.type}`,
    });

    headerIcons.push({
      ryecon: RyeconClock,
      text: `Created ${toRelativeDate(
        asset.created ? new Date(asset.created) : new Date(),
      )}`,
      tooltip: formatDateCommon(asset.created ? new Date(asset.created) : new Date())
        .humanized,
    });
  }

  const activeTab = tab.get('tab') ?? 'tables';

  const activeTabData = {
    data: activeTab === 'tables' ? tables : views,
    pageInfo: activeTab === 'tables' ? tablesPageInfo : viewsPageInfo,
    fetching: activeTab === 'tables' ? tablesFetching : viewsFetching,
    fetchMore: activeTab === 'tables' ? tablesFetchMore : viewsFetchMore,
  };

  return (
    <Page title="Asset Filter Page" pageType="assetDetails">
      <AssetBreadcrumbs />
      <div className="mb-2 flex justify-between">
        <div className="flex items-center gap-2">
          <Icon ryecon={RyeconColorSnowflake} size="lg" />
          <span className="text-xl font-bold">{asset?.name ?? ''}</span>
        </div>
        <div className="flex items-center gap-2">
          <FavoriteAssetToggle
            iconSize="md"
            assetId={asset?.urn ?? ''}
            assetType={asset?.type}
            platform={asset?.platform ?? ''}
            name={asset?.name ?? ''}
            isFavorite={isFavorite}
          />
          {asset && 'externalUrl' in asset && (
            <Link isInternal={false} to={asset?.externalUrl ?? ''} target="_blank">
              <Button size={'sm' as Sizes} text="Open in Snowflake" />
            </Link>
          )}
        </div>
      </div>
      <div
        className={twJoin(
          headerIcons.length ? 'opacity-100' : 'opacity-0',
          'mb-5 flex gap-4',
        )}
      >
        {headerIcons.map(({ key, ...props }: TextIconPair) => {
          if (props.href) {
            return (
              <Link key={props.text ?? key} isInternal={false} to={props.href}>
                <HeaderIconItem {...props} />
              </Link>
            );
          }
          return <HeaderIconItem key={props.text ?? key} {...props} />;
        })}
      </div>
      <FloatingTabs>
        <FloatingTabs.Tab
          id="tables"
          text={tabNameMap.tables}
          isActive={activeTab === 'tables'}
          onClick={(tab: TabType) => setTab({ tab })}
        />
        <FloatingTabs.Tab
          id="views"
          text={tabNameMap.views}
          isActive={activeTab === 'views'}
          onClick={(tab: TabType) => setTab({ tab })}
        />
      </FloatingTabs>
      <ResourceFilterTable
        pluralizedResultName={tabNameMap[activeTab as keyof typeof tabNameMap]}
        data={activeTab === 'tables' ? tables : views}
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
            id: 'name',
            minSize: 60,
            maxSize: 240,
            cell: ({ ...props }) => (
              <LinkCell
                href={catalogAssetLink({
                  accountId: accountId ?? 0,
                  assetId: props.row.original.urn,
                })}
                {...props}
              />
            ),
          },
          {
            header: 'Type',
            accessorKey: 'type',
          },
          ...(activeTab === 'tables'
            ? [
                {
                  header: 'Rows',
                  accessorKey: 'rowCount',
                },
                {
                  header: 'Bytes',
                  accessorKey: 'sizeInBytes',
                  cell: ({ getValue }: { getValue: () => number }) =>
                    formatBytes(getValue() ?? 0),
                },
              ]
            : []),
          {
            header: 'Created',
            accessorKey: 'created',
            cell: ({ getValue }) => {
              return (
                <Tooltip
                  content={
                    formatDateCommon(getValue() ? new Date(getValue()) : new Date())
                      .humanized
                  }
                >
                  {toRelativeDate(getValue() ? new Date(getValue()) : new Date())}
                </Tooltip>
              );
            },
          },
        ]}
        pageInfo={activeTabData.pageInfo}
        isLoading={activeTabData.fetching}
        isLoadingMore={activeTabData.fetching}
        pageSize={10}
        fetchMore={activeTabData.fetchMore}
      />
    </Page>
  );
};
